import React, { useEffect, useState } from 'react';
import { NodeProps, NodeResizer } from 'reactflow';
import { useBoardMultiSelected } from '../useBoardSelection';
import { ExternalLink, Link2 } from 'lucide-react';
import type { BoardLinkData } from '../../../types';
import { useBoardActions } from '../boardActions';
import NodeActionsToolbar from './NodeActionsToolbar';

export function linkVisibleTitle(data: BoardLinkData): string {
  return data.displayTitle || data.title || data.domain || data.url;
}

const LinkNode: React.FC<NodeProps<BoardLinkData>> = ({ id, data, selected }) => {
  const { updateNodeData, readOnly } = useBoardActions();
  const multiSelected = useBoardMultiSelected();
  const [editingTitle, setEditingTitle] = useState(false);
  const [draftTitle, setDraftTitle] = useState(data.displayTitle || '');

  useEffect(() => {
    setDraftTitle(data.displayTitle || '');
  }, [data.displayTitle]);

  useEffect(() => {
    if (!selected) setEditingTitle(false);
  }, [selected]);

  const visibleTitle = linkVisibleTitle(data);
  const showTitleEditor = (selected || editingTitle) && !readOnly && !data.loading && !multiSelected;

  const commitTitle = () => {
    setEditingTitle(false);
    const next = draftTitle.trim();
    if (next !== (data.displayTitle || '')) {
      updateNodeData(id, { displayTitle: next || undefined });
    }
  };

  return (
    <>
      <NodeResizer
        isVisible={!!selected && !readOnly}
        minWidth={180}
        minHeight={120}
        lineClassName="board-resize-line"
        handleClassName="board-resize-handle"
      />
      <NodeActionsToolbar id={id} />
      <div className={`board-node board-link-node ${selected ? 'is-selected' : ''}`}>
        {data.loading ? (
          <div className="board-link-loading">
            <Link2 size={18} />
            <span>Fetching preview…</span>
          </div>
        ) : (
          <div className="board-link-body">
            {data.thumbnailUrl && (
              <a
                href={data.url}
                target="_blank"
                rel="noopener noreferrer"
                className="board-link-thumb-link nodrag"
                title={data.url}
              >
                <div className="board-link-thumb">
                  <img src={data.thumbnailUrl} alt="" draggable={false} />
                </div>
              </a>
            )}
            <div className="board-link-meta">
              {showTitleEditor ? (
                <input
                  type="text"
                  className="board-link-title-input nodrag nowheel"
                  placeholder="Rename link…"
                  value={draftTitle}
                  onChange={(e) => setDraftTitle(e.target.value)}
                  onFocus={() => setEditingTitle(true)}
                  onBlur={commitTitle}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      commitTitle();
                      (e.target as HTMLInputElement).blur();
                    }
                    if (e.key === 'Escape') {
                      setDraftTitle(data.displayTitle || '');
                      setEditingTitle(false);
                      (e.target as HTMLInputElement).blur();
                    }
                  }}
                />
              ) : (
                <div
                  className="board-link-title"
                  onDoubleClick={() => {
                    if (!readOnly) setEditingTitle(true);
                  }}
                >
                  {visibleTitle}
                </div>
              )}
              {data.description && <div className="board-link-desc">{data.description}</div>}
              <a
                href={data.url}
                target="_blank"
                rel="noopener noreferrer"
                className="board-link-domain nodrag"
                title={data.url}
              >
                {data.favicon && <img src={data.favicon} alt="" className="board-link-favicon" />}
                <span>{data.domain}</span>
                <ExternalLink size={12} />
              </a>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default React.memo(LinkNode);
